/**
 * Text-to-Speech Test UI
 */

import { APIClient, TTSHistoryEntry } from './api';

export class TTSTestUI {
  // ... existing code ...

  private container: HTMLElement;
  private client: APIClient;
  private audioElement: HTMLAudioElement | null = null;
  private animationFrame: number | null = null;
  private currentVisemes: any[] = [];
  private readonly supportedVoiceLocales = ['ja-JP', 'en-US', 'zh-CN', 'ko-KR'];

  constructor(container: HTMLElement, client: APIClient) {
    this.container = container;
    this.client = client;
  }

  render(): void {
    this.container.innerHTML = `
      <div class="test-section">
        <h2>Text-to-Speech Test</h2>
        <p class="description">Test TTS model API (Azure, ElevenLabs)</p>

        <div class="tts-container">
          <label for="tts-text-input">Enter text to convert:</label>
          <textarea id="tts-text-input" placeholder="Hello, this is a test of the text to speech system." rows="4"></textarea>

          <div class="controls">
            <div class="control-group">
               <label>Provider:</label>
               <select id="tts-provider">
                 <option value="azure" selected>Azure Speech</option>
                 <option value="elevenlabs">ElevenLabs</option>
               </select>
            </div>

            <div class="control-group">
              <label>Voice:</label>
              <select id="tts-voice">
                <!-- Populated dynamically -->
              </select>
            </div>

            <div class="control-group" id="model-group" style="display: none;">
               <label>Model ID:</label>
               <select id="tts-model">
                 <option value="eleven_multilingual_v2" selected>Multilingual v2</option>
                 <option value="eleven_turbo_v2">Turbo v2</option>
                 <option value="eleven_turbo_v2_5">Turbo v2.5</option>
                 <option value="eleven_monolingual_v1">Monolingual v1</option>
               </select>
            </div>
            
            <div class="control-group">
               <label>Viseme Output:</label>
               <select id="tts-viseme-type">
                 <option value="facial-expression">Blend Shapes (3D JSON)</option>
                 <option value="svg">SVG (2D Animation)</option>
               </select>
            </div>

            <!-- Azure Controls -->
            <div id="azure-controls">
              <div class="control-group">
                <label for="tts-rate">Speed (Rate): <span id="rate-value">0.9</span>x</label>
                <input type="range" id="tts-rate" min="0.5" max="2.0" step="0.1" value="0.9">
              </div>

              <div class="control-group">
                <label for="tts-pitch">Pitch: <span id="pitch-value">0</span>%</label>
                <input type="range" id="tts-pitch" min="-50" max="50" step="5" value="0">
              </div>
            </div>

            <!-- ElevenLabs Controls -->
            <div id="elevenlabs-controls" style="display: none;">
              <div class="control-group">
                <label for="tts-stability">Stability: <span id="stability-value">0.5</span></label>
                <input type="range" id="tts-stability" min="0" max="1" step="0.01" value="0.5">
              </div>
              <div class="control-group">
                <label for="tts-similarity">Similarity: <span id="similarity-value">0.75</span></label>
                <input type="range" id="tts-similarity" min="0" max="1" step="0.01" value="0.75">
              </div>
               <div class="control-group">
                <label for="tts-style">Style: <span id="style-value">0</span></label>
                <input type="range" id="tts-style" min="0" max="1" step="0.01" value="0">
              </div>
              <div class="control-group">
                 <label>
                   <input type="checkbox" id="tts-speaker-boost" checked> Use Speaker Boost
                 </label>
              </div>
            </div>

            <div class="control-group">
               <label>
                 <input type="checkbox" id="tts-force"> Force Refresh (Bypass Cache)
               </label>
            </div>
          </div>

          <div class="button-group">
            <button id="tts-generate" class="btn btn-primary">Generate Speech</button>
            <button id="tts-history-btn" class="btn btn-secondary">Check History</button>
            <button id="tts-play" class="btn btn-secondary" disabled>Play</button>
            <button id="tts-download" class="btn btn-secondary" disabled>Download</button>
          </div>
        </div>

        <div id="tts-history-container" class="history-container" style="display: none; margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
            <h3>Version History</h3>
            <ul id="tts-history-list" style="list-style: none; padding: 0;"></ul>
        </div>

        <div id="tts-response" class="response-container" style="display: none;"></div>
      </div>
    `;

    this.setupEventListeners();
    this.updateVoiceList();
  }

  destroy(): void {
    this.container.innerHTML = '';
    if (this.audioElement) {
      this.audioElement.pause();
      URL.revokeObjectURL(this.audioElement.src);
      this.audioElement = null;
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  private setupEventListeners() {
    const generateButton = document.getElementById('tts-generate')!;
    const playButton = document.getElementById('tts-play')!;
    const downloadButton = document.getElementById('tts-download')!;
    const historyButton = document.getElementById('tts-history-btn')!;
    const rateInput = document.getElementById('tts-rate') as HTMLInputElement;
    const pitchInput = document.getElementById('tts-pitch') as HTMLInputElement;
    const providerSelect = document.getElementById('tts-provider') as HTMLSelectElement;

    generateButton.addEventListener('click', () => this.generateSpeech());
    historyButton.addEventListener('click', () => this.loadHistory());
    playButton.addEventListener('click', () => this.playSpeech());
    downloadButton.addEventListener('click', () => this.downloadSpeech());

    rateInput?.addEventListener('input', (e: Event) => {
      const val = (e.target as HTMLInputElement).value;
      document.getElementById('rate-value')!.textContent = val;
    });

    pitchInput?.addEventListener('input', (e: Event) => {
      const val = (e.target as HTMLInputElement).value;
      document.getElementById('pitch-value')!.textContent = val;
    });

    // ElevenLabs sliders
    const stabilityInput = document.getElementById('tts-stability') as HTMLInputElement;
    const similarityInput = document.getElementById('tts-similarity') as HTMLInputElement;
    const styleInput = document.getElementById('tts-style') as HTMLInputElement;

    stabilityInput?.addEventListener('input', (e: Event) => {
      document.getElementById('stability-value')!.textContent = (e.target as HTMLInputElement).value;
    });
    similarityInput?.addEventListener('input', (e: Event) => {
      document.getElementById('similarity-value')!.textContent = (e.target as HTMLInputElement).value;
    });
    styleInput?.addEventListener('input', (e: Event) => {
      document.getElementById('style-value')!.textContent = (e.target as HTMLInputElement).value;
    });

    providerSelect?.addEventListener('change', () => this.updateVoiceList());

    // Auto-load history on valid text input? (Optional, maybe just button for now)
    // textInput.addEventListener('blur', () => { if(textInput.value.length > 0) this.loadHistory(); });

    // Initial populate
    this.updateVoiceList();
  }

  private async loadHistory() {
    const textInput = document.getElementById('tts-text-input') as HTMLTextAreaElement;
    const historyContainer = document.getElementById('tts-history-container')!;
    const historyList = document.getElementById('tts-history-list')!;
    const text = textInput.value.trim();

    if (!text) return;

    historyContainer.style.display = 'block';
    historyList.innerHTML = '<li>Loading...</li>';

    try {
      const history = await this.client.getHistory(text);
      historyList.innerHTML = '';

      if (history.length === 0) {
        historyList.innerHTML = '<li>No history found for this text.</li>';
        return;
      }

      history.forEach((entry: TTSHistoryEntry) => {
        const li = document.createElement('li');
        li.style.cssText = 'display: flex; justify-content: space-between; align-items: center; padding: 10px; border-bottom: 1px solid #eee; background: white; margin-bottom: 5px; border-radius: 4px;';

        const date = new Date(entry.createdAt).toLocaleString();
        const provider = entry.provider || 'unknown';
        const params = entry.params || {};
        const voiceName = params.voice || 'Default';
        const rate = params.rate || 1;
        const pitch = params.pitch || 0;
        const format = (params.format || 'mp3').toUpperCase();
        const details = `${this.escapeHtml(provider.toUpperCase())} | Voice: ${this.escapeHtml(voiceName)} | Rate: ${rate}x | Pitch: ${pitch} | Format: ${format}`;

        const infoDiv = document.createElement('div');
        infoDiv.innerHTML = `<div><strong>${this.escapeHtml(date)}</strong></div><div style="font-size: 0.85em; color: #666;">${details}</div>`;

        const actionsDiv = document.createElement('div');
        actionsDiv.style.display = 'flex';
        actionsDiv.style.gap = '10px';

        // Restore/Play Button
        const playBtn = document.createElement('button');
        playBtn.textContent = 'Load & Play';
        playBtn.className = 'btn btn-small';
        playBtn.style.padding = '5px 10px';
        playBtn.style.fontSize = '0.8em';
        playBtn.onclick = () => this.restoreAndPlay(entry);

        // Delete Button
        const delBtn = document.createElement('button');
        delBtn.textContent = 'Delete';
        delBtn.className = 'btn btn-small btn-danger';
        delBtn.style.padding = '5px 10px';
        delBtn.style.fontSize = '0.8em';
        delBtn.style.backgroundColor = '#ff4444';
        delBtn.style.color = 'white';
        delBtn.onclick = async () => {
          if (confirm('Delete this version?')) {
            await this.client.deleteHistory(text, entry.cacheKey);
            this.loadHistory(); // Refresh
          }
        };

        actionsDiv.appendChild(playBtn);
        actionsDiv.appendChild(delBtn);

        li.appendChild(infoDiv);
        li.appendChild(actionsDiv);
        historyList.appendChild(li);
      });

    } catch (e) {
      historyList.innerHTML = '<li>Failed to load history</li>';
    }
  }

  private async restoreAndPlay(entry: TTSHistoryEntry) {
    // Restore UI controls
    if (entry.params) {
      const p = entry.params;
      if (p.rate) (document.getElementById('tts-rate') as HTMLInputElement).value = p.rate.toString();
      if (p.pitch) (document.getElementById('tts-pitch') as HTMLInputElement).value = p.pitch.toString();
      if (p.voice) (document.getElementById('tts-voice') as HTMLSelectElement).value = p.voice;
      if (entry.provider) (document.getElementById('tts-provider') as HTMLSelectElement).value = entry.provider;

      // Restore ElevenLabs params
      if (p.stability !== undefined) (document.getElementById('tts-stability') as HTMLInputElement).value = p.stability.toString();
      if (p.similarityBoost !== undefined) (document.getElementById('tts-similarity') as HTMLInputElement).value = p.similarityBoost.toString();
      if (p.style !== undefined) (document.getElementById('tts-style') as HTMLInputElement).value = p.style.toString();
      if (p.useSpeakerBoost !== undefined) (document.getElementById('tts-speaker-boost') as HTMLInputElement).checked = p.useSpeakerBoost;

      // Update labels
      document.getElementById('rate-value')!.textContent = (p.rate || '0.9').toString();
      document.getElementById('pitch-value')!.textContent = (p.pitch || '0').toString();
      if (p.stability !== undefined) document.getElementById('stability-value')!.textContent = p.stability.toString();
      if (p.similarityBoost !== undefined) document.getElementById('similarity-value')!.textContent = p.similarityBoost.toString();
      if (p.style !== undefined) document.getElementById('style-value')!.textContent = p.style.toString();

      this.updateVoiceList();
      // Re-select voice after list update
      if (p.voice) (document.getElementById('tts-voice') as HTMLSelectElement).value = p.voice;
    }

    // Load cached audio directly without regenerating
    const responseContainer = document.getElementById('tts-response')!;
    responseContainer.style.display = 'block';
    responseContainer.innerHTML = '<div class="loading">Loading cached audio...</div>';

    try {
      // Determine file extension based on format in params
      const format = entry.params?.format || 'mp3';
      const ext = format === 'pcm' ? 'pcm' : 'mp3';

      // Fetch audio from cache using the cache key
      const audioUrl = `${this.client['baseUrl']}/v1/tts/audio/${entry.cacheKey}.${ext}`;
      const lipSyncUrl = `${this.client['baseUrl']}/v1/tts/lipsync/${entry.cacheKey}.json`;

      // Get token for authenticated requests
      const token = this.client.getToken();
      if (!token) {
        throw new Error('Authentication required. Please sign in.');
      }

      const audioResponse = await fetch(audioUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!audioResponse.ok) {
        throw new Error('Cached audio not found');
      }

      const audioBlob = await audioResponse.blob();

      // For PCM format, use Web Audio API instead of <audio> element
      if (format === 'pcm') {
        await this.playPCMAudio(audioBlob, entry, lipSyncUrl, token);
        return;
      }

      const localAudioUrl = URL.createObjectURL(audioBlob);

      // Try to fetch lip sync data
      let lipSyncData = null;
      try {
        const lipSyncResponse = await fetch(lipSyncUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (lipSyncResponse.ok) {
          lipSyncData = await lipSyncResponse.json();
        }
      } catch (e) {
        console.warn('No lip sync data available');
      }

      this.currentVisemes = lipSyncData?.visemes || [];

      const sizeKB = (audioBlob.size / 1024).toFixed(2);
      const provider = entry.provider || 'cache';
      const visemeType = entry.params.visemeType || 'facial-expression';

      responseContainer.innerHTML = `
        <div class="response-success">
          <h4>Cached Audio Loaded</h4>
          <div class="response-meta">
            <div class="meta-item"><strong>Size:</strong> ${sizeKB} KB</div>
            <div class="meta-item"><strong>Provider:</strong> ${this.escapeHtml(provider)} (Cached)</div>
          </div>

          <div class="lipsync-visualizer" style="display: flex; gap: 20px; align-items: center; margin: 20px 0; background: #f5f5f5; padding: 20px; border-radius: 8px;">
            <div id="canvas-container" style="position: relative; width: 200px; height: 200px;">
                <canvas id="lip-canvas" width="200" height="200" style="background: white; border-radius: 50%; width: 200px; height: 200px; position: absolute; top:0; left:0;"></canvas>
                <div id="svg-overlay" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100px; height: 100px; pointer-events: none;"></div>
            </div>
            <div class="lipsync-info">
              ${lipSyncData
          ? `<div>✅ Lip Sync Data: ${this.currentVisemes.length} frames</div><div>Type: ${this.escapeHtml(visemeType)}</div>`
          : '<div>⚠️ No Lip Sync Data</div>'}
            </div>
          </div>

          <div class="audio-player">
            <audio id="tts-audio-player" controls src="${localAudioUrl}"></audio>
          </div>
        </div>
      `;

      // Setup audio player
      this.audioElement = document.getElementById('tts-audio-player') as HTMLAudioElement;
      const playButton = document.getElementById('tts-play') as HTMLButtonElement;
      const downloadButton = document.getElementById('tts-download') as HTMLButtonElement;

      if (this.audioElement) {
        playButton.disabled = false;
        downloadButton.disabled = false;

        // Auto-play the audio
        this.audioElement.play().catch(e => console.warn('Auto-play blocked:', e));

        // Setup animation if lip sync data exists
        if (this.currentVisemes.length > 0) {
          this.startAnimationLoop();
          this.audioElement.addEventListener('ended', () => {
            if (this.animationFrame !== null) {
              cancelAnimationFrame(this.animationFrame);
            }
            this.drawFace(null);
            const overlay = document.getElementById('svg-overlay');
            if (overlay) overlay.innerHTML = '';
          });
        }

        setTimeout(() => this.drawFace(null), 100);
      }
    } catch (error) {
      responseContainer.innerHTML = `
        <div class="response-error">
          <h4>Error</h4>
          <div class="error-message">${error instanceof Error ? this.escapeHtml(error.message) : 'Failed to load cached audio'}</div>
          <p style="margin-top: 10px; font-size: 0.9em;">The cached audio may have expired. Try generating new audio.</p>
        </div>
      `;
    }
  }

  private async updateVoiceList() {
    const providerSelect = document.getElementById('tts-provider') as HTMLSelectElement;
    const voiceSelect = document.getElementById('tts-voice') as HTMLSelectElement;
    const modelGroup = document.getElementById('model-group')!;
    const provider = providerSelect.value as 'azure' | 'elevenlabs';

    voiceSelect.innerHTML = '<option value="">Loading voices...</option>';
    voiceSelect.disabled = true;

    if (provider === 'azure') {
      modelGroup.style.display = 'none';
      document.getElementById('azure-controls')!.style.display = 'block';
      document.getElementById('elevenlabs-controls')!.style.display = 'none';
    } else if (provider === 'elevenlabs') {
      modelGroup.style.display = 'block';
      document.getElementById('azure-controls')!.style.display = 'none';
      document.getElementById('elevenlabs-controls')!.style.display = 'block';
    }

    try {
      const responses = await Promise.all(
        this.supportedVoiceLocales.map(language => this.client.getVoices(provider, language))
      );

      const mergedVoices = new Map<string, any>();
      responses.forEach(response => {
        const voices = response[provider] || [];
        voices.forEach((voice: any) => {
          if (!voice?.id) return;
          if (!mergedVoices.has(voice.id)) {
            mergedVoices.set(voice.id, voice);
          }
        });
      });

      const voices = Array.from(mergedVoices.values());

      voiceSelect.innerHTML = '';
      if (voices.length === 0) {
        const opt = document.createElement('option');
        opt.value = "";
        opt.textContent = "No voices found";
        voiceSelect.appendChild(opt);
      } else {
        voices.forEach((v: any) => {
          const opt = document.createElement('option');
          opt.value = v.id;
          const normalizedLanguage = this.normalizeVoiceLanguage(v.language);
          if (normalizedLanguage) {
            opt.dataset.language = normalizedLanguage;
          }
          opt.textContent = `${v.name} (${normalizedLanguage || v.language || 'unknown'} · ${v.gender || 'neutral'})`;
          voiceSelect.appendChild(opt);
        });
      }
    } catch (e) {
      console.error('Failed to update voice list', e);
      voiceSelect.innerHTML = '<option value="">Error loading voices</option>';
    } finally {
      voiceSelect.disabled = false;
    }
  }

  private async generateSpeech() {
    const textInput = document.getElementById('tts-text-input') as HTMLTextAreaElement;
    const voiceSelect = document.getElementById('tts-voice') as HTMLSelectElement;
    const visemeTypeSelect = document.getElementById('tts-viseme-type') as HTMLSelectElement;
    const responseContainer = document.getElementById('tts-response')!;
    const generateButton = document.getElementById('tts-generate') as HTMLButtonElement;
    const playButton = document.getElementById('tts-play') as HTMLButtonElement;
    const downloadButton = document.getElementById('tts-download') as HTMLButtonElement;
    const rateInput = document.getElementById('tts-rate') as HTMLInputElement;
    const pitchInput = document.getElementById('tts-pitch') as HTMLInputElement;
    const providerSelect = document.getElementById('tts-provider') as HTMLSelectElement;
    const modelSelect = document.getElementById('tts-model') as HTMLSelectElement;

    const text = textInput.value.trim();
    if (!text) {
      alert('Please enter text to convert');
      return;
    }

    const forceCheckbox = document.getElementById('tts-force') as HTMLInputElement;
    const force = forceCheckbox.checked;

    const voice = voiceSelect.value;
    const visemeType = visemeTypeSelect.value as 'facial-expression' | 'svg';
    const rate = parseFloat(rateInput.value);
    const pitch = parseInt(pitchInput.value, 10);
    const provider = providerSelect.value as 'azure' | 'elevenlabs';
    const modelId = provider === 'elevenlabs' ? modelSelect.value : undefined;
    const selectedVoiceOption = voiceSelect.options[voiceSelect.selectedIndex];
    const selectedLanguage = selectedVoiceOption?.dataset.language || undefined;

    // ElevenLabs params
    const stability = parseFloat((document.getElementById('tts-stability') as HTMLInputElement).value);
    const similarityBoost = parseFloat((document.getElementById('tts-similarity') as HTMLInputElement).value);
    const style = parseFloat((document.getElementById('tts-style') as HTMLInputElement).value);
    const useSpeakerBoost = (document.getElementById('tts-speaker-boost') as HTMLInputElement).checked;

    // Show loading state
    generateButton.disabled = true;
    generateButton.textContent = 'Generating...';
    playButton.disabled = true;
    downloadButton.disabled = true;
    responseContainer.style.display = 'block';
    responseContainer.innerHTML = '<div class="loading">Generating speech...</div>';

    try {
      const startTime = Date.now();
      const ttsResponse = await this.client.textToSpeech(
        text,
        voice,
        force,
        visemeType,
        rate,
        pitch,
        provider,
        modelId,
        selectedLanguage,
        provider === 'elevenlabs' ? { stability, similarityBoost, style, useSpeakerBoost } : undefined
      );

      // Fetch the actual audio blob from the signed/public URL
      const token = this.client.getToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const audioFetchResponse = await fetch(ttsResponse.audioUrl, {
        headers
      });

      if (!audioFetchResponse.ok) {
        throw new Error(`Failed to fetch audio from URL: ${ttsResponse.audioUrl}`);
      }
      const audioBlob = await audioFetchResponse.blob();

      const duration = Date.now() - startTime;

      // Create audio URL from blob
      const localAudioUrl = URL.createObjectURL(audioBlob);

      this.currentVisemes = ttsResponse.lipSyncData?.visemes || [];

      const sizeKB = (audioBlob.size / 1024).toFixed(2);

      responseContainer.innerHTML = `
        <div class="response-success">
          <h4>Speech Generated</h4>
          <div class="response-meta">
            <div class="meta-item"><strong>Duration:</strong> ${duration}ms</div>
            <div class="meta-item"><strong>Size:</strong> ${sizeKB} KB</div>
            <div class="meta-item"><strong>Provider:</strong> ${this.escapeHtml(ttsResponse.provider)} ${ttsResponse.cached ? '(Cached)' : ''}</div>
          </div>

          <div class="lipsync-visualizer" style="display: flex; gap: 20px; align-items: center; margin: 20px 0; background: #f5f5f5; padding: 20px; border-radius: 8px;">
            <div id="canvas-container" style="position: relative; width: 200px; height: 200px;">
                <canvas id="lip-canvas" width="200" height="200" style="background: white; border-radius: 50%; width: 200px; height: 200px; position: absolute; top:0; left:0;"></canvas>
                <div id="svg-overlay" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100px; height: 100px; pointer-events: none;"></div>
            </div>
            <div class="lipsync-info">
              ${ttsResponse.lipSyncData
          ? `<div>✅ Lip Sync Data: ${this.currentVisemes.length} frames</div><div>Type: ${this.escapeHtml(visemeType)}</div>`
          : '<div>⚠️ No Lip Sync Data</div>'}
            </div>
          </div>

          <div class="audio-player">
            <audio id="tts-audio-player" controls src="${localAudioUrl}"></audio>
          </div>
        </div>
      `;

      // Get the DOM element we just created
      this.audioElement = this.container.querySelector('#tts-audio-player') as HTMLAudioElement;

      if (this.audioElement) {
        this.audioElement.addEventListener('play', () => this.startAnimationLoop());
        this.audioElement.addEventListener('pause', () => { if (this.animationFrame) cancelAnimationFrame(this.animationFrame); });
        this.audioElement.addEventListener('ended', () => {
          if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
          this.drawFace(null);
          const overlay = document.getElementById('svg-overlay');
          if (overlay) overlay.innerHTML = '';
        });
      }

      setTimeout(() => this.drawFace(null), 100);

      playButton.disabled = false;
      downloadButton.disabled = false;
    } catch (error) {
      responseContainer.innerHTML = `
        <div class="response-error">
          <h4>Error</h4>
          <div class="error-message">${error instanceof Error ? this.escapeHtml(error.message) : 'Unknown error'}</div>
        </div>
      `;
    } finally {
      generateButton.disabled = false;
      generateButton.textContent = 'Generate Speech';
    }
  }

  private startAnimationLoop() {
    if (!this.audioElement || !this.currentVisemes.length) return;

    const loop = () => {
      if (!this.audioElement || this.audioElement.paused) return;
      const currentTime = this.audioElement.currentTime;

      // Find active viseme
      const currentViseme = this.currentVisemes.reduce((prev, curr) => {
        return (curr.time <= currentTime && curr.time >= prev.time) ? curr : prev;
      }, { time: 0, visemeId: 0, blendShape: 'sil' });

      this.drawFace(currentViseme);
      this.animationFrame = requestAnimationFrame(loop);
    };

    this.animationFrame = requestAnimationFrame(loop);
  }

  private drawFace(viseme: any) {
    const canvas = document.getElementById('lip-canvas') as HTMLCanvasElement;
    const svgOverlay = document.getElementById('svg-overlay');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;

    // SVG Handling
    if (viseme?.animation && viseme.animation.startsWith('<svg')) {
      if (svgOverlay) svgOverlay.innerHTML = viseme.animation;
    } else {
      if (svgOverlay) svgOverlay.innerHTML = '';
    }

    // Canvas Face Drawing
    ctx.clearRect(0, 0, width, height);

    // Draw Face Outline
    ctx.beginPath();
    ctx.arc(cx, cy, 90, 0, Math.PI * 2);
    ctx.fillStyle = '#FFDDCC';
    ctx.fill();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Eyes
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(cx - 30, cy - 20, 8, 0, Math.PI * 2);
    ctx.arc(cx + 30, cy - 20, 8, 0, Math.PI * 2);
    ctx.fill();

    // If we have SVG active, don't draw canvas mouth
    if (viseme?.animation && viseme.animation.startsWith('<svg')) return;

    // Draw Canvas Mouth (ID based or BlendShape based)
    this.drawMouth(ctx, cx, cy + 30, viseme);
  }

  private drawMouth(ctx: CanvasRenderingContext2D, x: number, y: number, viseme: any) {
    ctx.fillStyle = '#A00';
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;

    let w = 40;
    let h = 2;

    if (viseme) {
      if (viseme.animation && viseme.animation.startsWith('{')) {
        // BLEND SHAPE MODE (JSON)
        try {
          const anim = JSON.parse(viseme.animation);
          // Azure BlendShapes[0] is often the filtered list.
          // We need specific indices. Standard ARKit/Azure mappings:
          // 19: JawOpen, 20: MouthClose, 21: MouthFunnel, 22: MouthPucker, 23: MouthLeft...
          // Note: The array layout depends on the request. But typically it's the 55 ARKit shapes.
          // Let's assume standard order.

          if (anim.BlendShapes && anim.BlendShapes.length > 0) {
            const shapes = anim.BlendShapes[0]; // Assuming first frame of this chunk
            const jawOpen = shapes[19] || 0;
            const mouthPucker = shapes[22] || 0;
            const mouthFunnel = shapes[21] || 0;

            // Drive width/height
            // JawOpen drives Height: 0 -> 2, 1 -> 40
            h = 2 + (jawOpen * 38);

            // Pucker/Funnel drives Width: 0 -> 40, 1 -> 20 (narrower)
            w = 40 - ((mouthPucker + mouthFunnel) * 20);
          }
        } catch (e) {
          console.warn('Failed to parse animation JSON', e);
        }
      } else {
        // FALLBACK / VISEME ID MODE
        const id = viseme.visemeId;
        if ([1, 2, 3, 4, 9, 11].includes(id)) { h = 25; w = 45; }
        else if ([15, 16, 20].includes(id)) { h = 8; w = 50; }
        else if ([12].includes(id)) { h = 10; w = 40; }
        else if ([13, 14, 18, 19].includes(id)) { h = 8; w = 40; }
        else if ([6, 21].includes(id)) {
          if (id === 6) { h = 6; w = 50; }
          else { h = 2; w = 35; }
        } else if ([5, 7, 8, 10].includes(id)) { h = 20; w = 25; }
      }
    }

    ctx.beginPath();
    ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  private playSpeech() {
    if (this.audioElement) {
      this.audioElement.currentTime = 0;
      this.audioElement.play();
    }
  }

  private downloadSpeech() {
    if (this.audioElement) {
      const a = document.createElement('a');
      a.href = this.audioElement.src;
      a.download = 'speech.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  private async playPCMAudio(audioBlob: Blob, entry: TTSHistoryEntry, lipSyncUrl: string, token: string) {
    const responseContainer = document.getElementById('tts-response')!;

    try {
      // Fetch lip sync data
      let lipSyncData = null;
      try {
        const lipSyncResponse = await fetch(lipSyncUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (lipSyncResponse.ok) {
          lipSyncData = await lipSyncResponse.json();
        }
      } catch (e) {
        console.warn('No lip sync data available');
      }

      this.currentVisemes = lipSyncData?.visemes || [];

      const sizeKB = (audioBlob.size / 1024).toFixed(2);
      const provider = entry.provider || 'cache';
      const visemeType = entry.params.visemeType || 'facial-expression';

      responseContainer.innerHTML = `
        <div class="response-success">
          <h4>Cached Audio Loaded (PCM)</h4>
          <div class="response-meta">
            <div class="meta-item"><strong>Size:</strong> ${sizeKB} KB</div>
            <div class="meta-item"><strong>Provider:</strong> ${this.escapeHtml(provider)} (Cached)</div>
            <div class="meta-item"><strong>Format:</strong> PCM 24kHz 16-bit</div>
          </div>

          <div class="lipsync-visualizer" style="display: flex; gap: 20px; align-items: center; margin: 20px 0; background: #f5f5f5; padding: 20px; border-radius: 8px;">
            <div id="canvas-container" style="position: relative; width: 200px; height: 200px;">
                <canvas id="lip-canvas" width="200" height="200" style="background: white; border-radius: 50%; width: 200px; height: 200px; position: absolute; top:0; left:0;"></canvas>
                <div id="svg-overlay" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100px; height: 100px; pointer-events: none;"></div>
            </div>
            <div class="lipsync-info">
              ${lipSyncData
          ? `<div>✅ Lip Sync Data: ${this.currentVisemes.length} frames</div><div>Type: ${this.escapeHtml(visemeType)}</div>`
          : '<div>⚠️ No Lip Sync Data</div>'}
            </div>
          </div>

          <div class="audio-player">
            <button id="play-pcm-btn" class="btn btn-primary">Play PCM Audio</button>
          </div>
        </div>
      `;

      // Setup PCM playback using Web Audio API
      const playBtn = document.getElementById('play-pcm-btn')!;
      playBtn.addEventListener('click', async () => {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        const pcmData = await audioBlob.arrayBuffer();

        // Convert Int16 PCM to Float32 for Web Audio API
        const int16 = new Int16Array(pcmData);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          float32[i] = int16[i] / 32768.0;
        }

        // Create and play audio buffer
        const audioBuf = audioContext.createBuffer(1, float32.length, 24000);
        audioBuf.copyToChannel(float32, 0);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuf;
        source.connect(audioContext.destination);
        source.start(0);

        // Setup animation
        this.audioElement = null; // Not using <audio> element for PCM
        this.startAnimationLoop();
        source.onended = () => {
          if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
          this.drawFace(null);
        };
      });

      setTimeout(() => this.drawFace(null), 100);

    } catch (error) {
      responseContainer.innerHTML = `
        <div class="response-error">
          <h4>Error</h4>
          <div class="error-message">${error instanceof Error ? this.escapeHtml(error.message) : 'Unknown error'}</div>
        </div>
      `;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private normalizeVoiceLanguage(language?: string): string | undefined {
    if (!language) return undefined;
    const normalized = language.toLowerCase();
    if (normalized === 'ja') return 'ja-JP';
    if (normalized === 'en') return 'en-US';
    if (normalized === 'zh') return 'zh-CN';
    if (normalized === 'ko') return 'ko-KR';
    return language;
  }
}
